import { client as axios } from "./axiosClient";
import { Constants } from "@socialcoach/services";
import { UserContext, catchServiceException } from "@socialcoach/services";
import * as DTO from "../interfaces";
import { scCatchServiceException } from "@/annotations";

export class SocialCoachSessionService {
  @scCatchServiceException()
  static async updatePlayer(payload: DTO.UpdateUserPayload): Promise<DTO.UserRegistered> {
    const response = await axios.put<DTO.UserRegistered>(
      `${Constants.API_URL}/accounts/update`,
      {
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
        photoUrl: payload.photoUrl,
        socialNetworks: payload.socialNetworks,
        title: payload.title,
        complianceDisclaimer: payload.complianceDisclaimer,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @scCatchServiceException()
  static async saveAccessToken(accessToken: string, networkId: DTO.SocialNetworksId): Promise<any> {
    const response = await axios.post<any>(
      `${Constants.API_URL}/accounts/profiles/add-social-network`,
      {
        accessToken,
        networkId,
      },
      {
        withCredentials: false,
      }
    );

    return response.data;
  }

  @scCatchServiceException()
  static async addSocialNetwork(payload: DTO.AddSocialTokenRequest): Promise<DTO.SocialNetwork> {
    const response = await axios.post<DTO.SocialNetwork>(
      `${Constants.API_URL}/accounts/profiles/complete-social-network`,
      {
        ...payload,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async updateCoach(
    coachId: string,
    payload: DTO.BaseUpdateCoach,
    userId?: number
  ): Promise<DTO.ResponseUpdateCoach> {
    const response = await axios.put<DTO.ResponseUpdateCoach>(
      `${Constants.API_URL}/accounts/coach/${coachId}?userId=${userId}`,
      {
        ...payload,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async removeSocialNetwork(socialNetworkID: DTO.SocialNetworksId, profileId: string): Promise<any> {
    const response = await axios.delete<any>(
      `${Constants.API_URL}/accounts/profiles/${profileId}/social-networks/${socialNetworkID}`,
      {
        withCredentials: false,
      }
    );
    return response.data;
  }

  @catchServiceException()
  static async resetPasswordRequest(email: string): Promise<void> {
    await axios.put<void>(
      `${Constants.API_URL}/accounts/send-change-password?email=${encodeURIComponent(email)}`,
      null,
      {
        withCredentials: false,
      }
    );
  }

  @catchServiceException()
  static async sendWelcomeEmail(coachId: string | number, userId: string | number): Promise<void> {
    await axios.put<void>(
      `${Constants.API_URL}/accounts/send-welcome-email?coachId=${encodeURIComponent(
        coachId
      )}&userId=${encodeURIComponent(userId)}`,
      null,
      {
        withCredentials: true,
      }
    );
  }

  @catchServiceException()
  static async changePassword(userId: string, actionToken: string, newPassword: string): Promise<void> {
    await axios.put<void>(
      `${Constants.API_URL}/accounts/change-password`,
      {
        userId,
        token: actionToken,
        password: newPassword,
      },
      {
        withCredentials: false,
      }
    );
  }

  @catchServiceException()
  static async userChangePassword(oldPassword: string, newPassword: string): Promise<void> {
    await axios.put<void>(
      `${Constants.API_URL}/accounts/change-my-password`,
      {
        oldPassword,
        newPassword,
      },
      {
        withCredentials: true,
      }
    );
  }

  @catchServiceException()
  static async impersonate(username: string): Promise<void> {
    await axios.post<void>(
      `${Constants.API_URL}/auth/impersonate`,
      {
        username,
      },
      {
        withCredentials: true,
      }
    );
  }

  @catchServiceException()
  static async getUserContext(skipAuthRedirect?: boolean): Promise<UserContext> {
    const response = await axios.get<UserContext>(`${Constants.API_URL}/accounts/me`, {
      withCredentials: true,
      ...({ skipAuthRedirect } as any),
    });
    return response.data;
  }
}
